/**
 * The covers browse surface — grouping, and nothing else.
 *
 * The album table and the covers grid below it are the SAME query seen two ways
 * (design/README.md §5). So the grouping is arithmetic over the rows the table already has,
 * not a second fetch with a second set of rules, and it lives here where it can be tested
 * rather than inside a component.
 *
 * Deliberately not here: the spine/shelf view. It was rejected outright
 * (docs/DESIGN-CONFLICTS.md D1) and covers are the browse surface.
 */
/** Shown where a facet has no value. Never blank, never "null", never a silent drop. */
export const NO_YEAR = "NO YEAR";
export const NO_GENRE = "NO GENRE";
export const UNRATED_BAND = "UNRATED";
/** The decade an album belongs to: 1998 → 1990. */
export function decadeOf(year) {
    return year === null ? null : Math.floor(year / 10) * 10;
}
export function decadeLabel(year) {
    const d = decadeOf(year);
    return d === null ? NO_YEAR : `${d}s`;
}
/**
 * Score bands are whole-number buckets, top-open at 10.
 *
 * 10.0 gets its own band rather than sharing with 9.x: it is the ceiling, and the ceiling being
 * reachable by rating is the point (docs/DESIGN-CONFLICTS.md §J2). An unrated album has no band —
 * it is not a zero and it does not fall into the bottom bucket.
 */
export function scoreBandLabel(score) {
    if (score === null)
        return UNRATED_BAND;
    const floor = Math.floor(score);
    if (floor >= 10)
        return "10.0";
    return `${floor}.0 – ${floor}.9`;
}
/** Sorts a band label; higher is better, unrated always last. */
function bandOrder(score) {
    return score === null ? Number.NEGATIVE_INFINITY : Math.min(10, Math.floor(score));
}
/**
 * Groups albums for the covers grid.
 *
 * Groups are ordered by the facet's own logic — decades ascending because that is how a shelf
 * reads, bands descending because the best is the point — and within a group the covers are
 * ordered by score with the unrated pinned last, exactly as the table above orders them.
 */
export function browseGroups(items, facet, get) {
    const buckets = new Map();
    for (const item of items) {
        let label;
        let order;
        let missing = false;
        switch (facet) {
            case "decade": {
                const d = decadeOf(get.year(item));
                label = d === null ? NO_YEAR : `${d}s`;
                order = d ?? 0;
                missing = d === null;
                break;
            }
            case "genre": {
                const g = get.genre(item);
                label = g ?? NO_GENRE;
                order = 0;
                missing = g === null;
                break;
            }
            case "artist": {
                label = get.artist(item);
                order = 0;
                break;
            }
            case "band": {
                const s = get.score(item);
                label = scoreBandLabel(s);
                order = bandOrder(s);
                missing = s === null;
                break;
            }
        }
        const existing = buckets.get(label);
        if (existing)
            existing.items.push(item);
        else
            buckets.set(label, { label, order, missing, items: [item] });
    }
    const groups = [...buckets.values()];
    groups.sort((a, b) => {
        // A missing value is never interleaved with real ones — it sorts to the end whatever the facet.
        if (a.missing !== b.missing)
            return a.missing ? 1 : -1;
        if (facet === "decade")
            return a.order - b.order;
        if (facet === "band")
            return b.order - a.order;
        return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
    });
    for (const group of groups) {
        group.items = [...group.items].sort((a, b) => {
            const sa = get.score(a);
            const sb = get.score(b);
            if (sa === null && sb === null)
                return 0;
            if (sa === null)
                return 1;
            if (sb === null)
                return -1;
            return sb - sa;
        });
    }
    return groups.map((g) => ({ key: g.label, label: g.label, items: g.items }));
}
/**
 * The A–Z jump rail: 27 targets, `#` then A–Z, always the same 27.
 *
 * A rail whose letters appear and disappear with the library is a rail you cannot aim at — the
 * muscle memory for "jump to S" stops working the moment the only S album is deleted. So every
 * letter is always a target; the empty ones are simply quiet.
 */
export const JUMP_LETTERS = ["#", ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))];
/** Leading articles, dropped for filing. "The Rip" files under R, the way a shelf does. */
const LEADING_ARTICLE = /^(the|a|an)\s+/i;
/**
 * Which rail bucket a title belongs to.
 *
 * Diacritics fold — "Björk" files under B, not under `#`, because a rail that sends every accented
 * title to the punctuation bucket makes the punctuation bucket the largest one in a music library.
 * Anything that still is not a letter after folding (a numeral, a bracket, an emoji) lands on `#`,
 * which is what `#` is for.
 */
export function jumpLetter(title) {
    const first = filingKey(title).charAt(0).toUpperCase();
    return /^[A-Z]$/.test(first) ? first : "#";
}
/**
 * The string a title FILES under — the same normalisation the rail letter comes from.
 *
 * It has to be the same one, or the rail lies: sorting on the raw title puts "The Rip" under T
 * while the rail files it under R, and the jump lands somewhere near the wrong letter. One rule,
 * used for both the bucket and the order.
 */
export function filingKey(title) {
    return title
        .trim()
        .replace(LEADING_ARTICLE, "")
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase();
}
/** Compares two titles the way the library files them. Ties fall back to the raw title. */
export function byFiling(a, b) {
    return filingKey(a).localeCompare(filingKey(b)) || a.localeCompare(b);
}
//# sourceMappingURL=browse.js.map